import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import { SEOHead } from '@/components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function IntakeSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (response.data.payment_status === 'paid') {
          setPaymentVerified(true);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
      } finally {
        setLoading(false);
      }
    };

    checkPaymentStatus();
    
    // Poll a few times to ensure payment is confirmed
    const pollInterval = setInterval(() => {
      checkPaymentStatus();
    }, 2000);

    // Stop polling after 10 seconds
    setTimeout(() => {
      clearInterval(pollInterval);
      setLoading(false);
    }, 10000);

    return () => clearInterval(pollInterval);
  }, [sessionId]);

  return (
    <div className="bg-zinc-950 min-h-screen flex items-center">
      <SEOHead 
        title="Protocol Initiated | OnPoint WFM Analytics Lab"
        description="Your intake submission and payment have been received."
      />
      <div className="container-site py-32 max-w-2xl mx-auto text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-500/10 border border-amber-500/20 mb-8">
          {loading ? (
            <Loader2 size={24} className="text-amber-500 animate-spin" />
          ) : (
            <CheckCircle size={24} className="text-amber-500" />
          )}
        </div>
        <span className="text-xs font-mono tracking-widest uppercase text-amber-500 block mb-5">
          {loading ? 'Confirming Payment...' : 'Submission Received'}
        </span>
        <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-zinc-100 mb-6">
          Protocol Initiated.
        </h1>
        
        {/* Payment confirmation line - only shown when payment is verified */}
        {paymentVerified && (
          <p className="text-base text-amber-400 leading-relaxed mb-4 bg-amber-500/5 border border-amber-500/20 rounded-md px-4 py-3" data-testid="payment-confirmed-message">
            Payment received. We will confirm one of your proposed time slots by email.
          </p>
        )}
        
        <p className="text-base text-zinc-400 leading-relaxed mb-4">
          Your intake submission has been received. We will review your organizational context and
          data readiness parameters and respond within 1–2 business days.
        </p>
        <p className="text-sm text-zinc-500 leading-relaxed mb-10">
          If your submission included urgent timeline requirements, those will be factored into our response.
          No data has been transferred at this stage. Data submission occurs only after engagement confirmation.
        </p>
        <div className="border border-zinc-800 rounded-md p-6 bg-zinc-900/20 text-left mb-10">
          <h3 className="text-sm font-medium text-zinc-300 mb-3">What happens next</h3>
          <ul className="space-y-2">
            {[
              'We review your intake submission (1–2 business days)',
              'We confirm your Pre-Diagnostics Call time slot by email',
              'Pre-Diagnostics Call conducted (60–90 minutes)',
              'You submit your interval-level export via secure file transfer',
              'Classification begins on confirmed receipt (3 weeks)',
              'Deliverable delivered via secure link',
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="text-xs font-mono text-amber-500/70 mt-0.5 flex-shrink-0">{String(i + 1).padStart(2, '0')}</span>
                <span className="text-sm text-zinc-400">{step}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/"
            data-testid="success-home-link"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-zinc-100 text-zinc-900 rounded-md text-sm font-medium hover:bg-white transition-colors duration-200"
          >
            Return Home <ArrowRight size={14} />
          </Link>
          <Link
            to="/faq"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-zinc-700 text-zinc-300 rounded-md text-sm hover:border-zinc-500 hover:text-white transition-colors duration-200"
          >
            Review FAQ
          </Link>
        </div>
      </div>
    </div>
  );
}
